import React from 'react';
import styled, { keyframes } from 'styled-components';
import { COLORS } from '../../../constants';

interface ProgressBarProps {
  percentage: number;
  height?: string;
  backgroundColor?: string;
  fillColor?: string;
  animated?: boolean;
  showLabel?: boolean;
  label?: string;
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const ProgressContainer = styled.div<{ height: string; backgroundColor: string }>`
  width: 100%;
  height: ${props => props.height};
  background-color: ${props => props.backgroundColor};
  border-radius: ${props => `calc(${props.height} / 2)`};
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressFill = styled.div<{ 
  percentage: number; 
  fillColor: string; 
  animated: boolean;
  height: string;
}>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.fillColor};
  border-radius: ${props => `calc(${props.height} / 2)`};
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  ${props => props.animated && `
    background: linear-gradient(
      90deg,
      ${props.fillColor} 25%,
      rgba(255, 255, 255, 0.2) 50%,
      ${props.fillColor} 75%
    );
    background-size: 200px 100%;
    animation: ${shimmer} 2s infinite;
  `}
  
  /* Effet de brillance subtil */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.3),
      rgba(255, 255, 255, 0.1)
    );
    border-radius: ${props => `calc(${props.height} / 2) calc(${props.height} / 2) 0 0`};
  }
`;

const ProgressLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: 600;
  color: ${COLORS.DARK};
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  z-index: 2;
`;

const ProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  height = '12px',
  backgroundColor = COLORS.PRIMARY + '20',
  fillColor = COLORS.PRIMARY,
  animated = false,
  showLabel = false,
  label
}) => {
  const clampedPercentage = Math.min(100, Math.max(0, percentage));
  
  return (
    <ProgressContainer height={height} backgroundColor={backgroundColor}>
      <ProgressFill
        percentage={clampedPercentage}
        fillColor={fillColor}
        animated={animated}
        height={height}
      />
      {showLabel && (
        <ProgressLabel>
          {label || `${Math.round(clampedPercentage)}%`}
        </ProgressLabel>
      )}
    </ProgressContainer>
  );
};

export default ProgressBar; 